import React, { useRef, useState } from 'react';

export enum UploadError {
  INVALID_TYPE = 'invalidType',
  INVALID_SIZE = 'invalidSize'
}

const MAX_IMAGE_SIZE_BYTES = 250 * 1024; // 250 KB
const MAX_VIDEO_SIZE_BYTES = 60 * 1024 * 1024; // 60 MB

// Extensions
const EXT_WHITELIST = new Set(['jpg', 'jpeg', 'png', 'mp4']);

// MIME types
const MIME_WHITELIST = new Set(['image/jpeg', 'image/png', 'video/mp4']);

const getExt = (name: string) => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
};

const isValidType = (file: File) => {
  const mimeOk = file.type && MIME_WHITELIST.has(file.type);
  const extOk = EXT_WHITELIST.has(getExt(file.name));
  return mimeOk || extOk;
};

const isValidSize = (file: File) => {
  const ext = getExt(file.name);
  if (ext === 'mp4') {
    return file.size <= MAX_VIDEO_SIZE_BYTES;
  }
  return file.size <= MAX_IMAGE_SIZE_BYTES;
};

export const useFileUpload = () => {
  const [isDragging, setIsDragging] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<UploadError | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const openPicker = () => inputRef.current?.click();

  const removeFile = () => {
    setFile(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const processFile = (file: File | undefined) => {
    if (!file) return;

    if (!isValidType(file)) {
      setError(UploadError.INVALID_TYPE);
      setFile(null);
      return;
    }
    if (!isValidSize(file)) {
      setError(UploadError.INVALID_SIZE);
      setFile(null);
      return;
    }

    setError(null);
    setFile(file);
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    processFile(file);
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    processFile(file);
  };

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const inputEl = (
    <input
      data-testid="file-input"
      ref={inputRef}
      type="file"
      accept=".jpg,.jpeg,.png,.mp4"
      style={{
        display: 'none'
      }}
      onChange={handleChange}
    />
  );

  return {
    error,
    file,
    inputEl,
    isDragging,
    openPicker,
    removeFile,
    handleDrop,
    handleDragOver,
    handleDragLeave
  };
};
